import menuAim from 'menu-aim'

interface ScrollContainer {
  wrapper: HTMLElement
  topIndicator: HTMLElement
  bottomIndicator: HTMLElement
}

up.compiler('[data-menu]', (element) => {
  const container = element.querySelector('.dropdown--container')!
  const menus = container.querySelectorAll('.dropdown--menu')
  const mobileHeaderClose = container.querySelector('.dropdown--mobile-header-close')!
  const mobileHeaderBack = container.querySelector('.dropdown--mobile-header-back')!
  const mobileHeaderText = container.querySelector('.dropdown--mobile-header-text')!
  const mobileIndicator = container.querySelector('.dropdown--mobile-indicator')!

  const mobileHeaderTexts: string[] = []
  const openItems: Element[] = []
  let lastOpenedLevel = 1

  const scrollContainers = new Map<Element, ScrollContainer>()

  menus.forEach((menu) => {
    scrollContainers.set(menu, setupScrollContainer(menu as HTMLElement))
    initMenu(menu)
  })

  const containerObserver = new MutationObserver(() => {
    if (container.classList.contains('-show')) {
      requestAnimationFrame(updateAllScrollContainers)
    }
  })
  containerObserver.observe(container, { attributes: true, attributeFilter: ['class'] })

  window.addEventListener('resize', updateAllScrollContainers)

  mobileHeaderBack.addEventListener('click', (event) => {
    const lastOpenItem = openItems[openItems.length - 1]
    setMenuOpen(lastOpenItem, false)
    event.preventDefault()
    event.stopPropagation()
  })

  mobileHeaderClose.addEventListener('click', closeAllOpenItems)

  function setupScrollContainer(menu: HTMLElement): ScrollContainer {
    const wrapper = document.createElement('div')
    wrapper.classList.add('dropdown--scroll-wrapper')

    while (menu.firstChild) {
      wrapper.appendChild(menu.firstChild)
    }

    const topIndicator = document.createElement('div')
    topIndicator.classList.add('dropdown--scroll-indicator', '-top', '-hidden')

    const bottomIndicator = document.createElement('div')
    bottomIndicator.classList.add('dropdown--scroll-indicator', '-bottom', '-hidden')

    wrapper.insertBefore(topIndicator, wrapper.firstChild)
    wrapper.appendChild(bottomIndicator)
    menu.appendChild(wrapper)

    wrapper.addEventListener(
      'scroll',
      () => {
        updateScrollIndicators({ wrapper, topIndicator, bottomIndicator })
      },
      { passive: true },
    )

    return { wrapper, topIndicator, bottomIndicator }
  }

  function updateScrollContainerMaxHeight(menu: HTMLElement, scrollContainer: ScrollContainer) {
    const rect = menu.getBoundingClientRect()
    if (rect.height === 0) return
    const bottomMargin = 16
    const maxHeight = window.innerHeight - rect.top - bottomMargin
    scrollContainer.wrapper.style.maxHeight = `${Math.max(maxHeight, 200)}px`
  }

  function updateScrollIndicators(scrollContainer: ScrollContainer) {
    const { wrapper, topIndicator, bottomIndicator } = scrollContainer
    const canScrollUp = wrapper.scrollTop > 1
    const canScrollDown = wrapper.scrollTop + wrapper.clientHeight < wrapper.scrollHeight - 1

    topIndicator.classList.toggle('-hidden', !canScrollUp)
    bottomIndicator.classList.toggle('-hidden', !canScrollDown)
  }

  function updateAllScrollContainers() {
    scrollContainers.forEach((scrollContainer, menu) => {
      updateScrollContainerMaxHeight(menu as HTMLElement, scrollContainer)
      updateScrollIndicators(scrollContainer)
    })
  }

  function initMenu(menu: Element) {
    menuAim(menu, {
      rowSelector: ':scope > .dropdown--scroll-wrapper > li',
      activate: (item: Element) => {
        isDesktopInteraction() && setMenuOpen(item, true)
      },
      deactivate: (item: Element) => {
        isDesktopInteraction() && setMenuOpen(item, false)
      },
      exitMenu: () => {
        return true
      },
    })

    up.on(menu, 'mousedown click', '.dropdown--menu-item', (event, item) => {
      if (isMobileInteraction()) {
        const menuLevel = item!.getAttribute('data-menu-level')

        if (item!.querySelector('.dropdown--menu')) {
          if (isNextLevel(menuLevel)) {
            setMenuOpen(item!, true)
          }
          event.preventDefault()
          event.stopPropagation()
        } else if (event.type === 'click') {
          // Only close on the actual click, not mousedown: closing slides the
          // parent submenu away via a CSS transition, and doing that while
          // the mouse button is still down moves the link out from under the
          // pointer before mouseup — the browser then can't resolve the click
          // to this link at all, silently dropping the navigation.
          for (const openItem of openItems) {
            setMenuOpen(openItem, false)
          }
        }
      }
    })

    up.on(menu, 'keydown', '.dropdown--menu-item', (event: KeyboardEvent, item: HTMLElement) => {
      if (event.key === 'ArrowRight') {
        const menuLevel = item!.getAttribute('data-menu-level')

        if (item!.querySelector('.dropdown--menu')) {
          if (isNextLevel(menuLevel)) {
            setMenuOpen(item!, true)
          }
          event.preventDefault()
          event.stopPropagation()
        }
      } else if (event.key === 'ArrowLeft') {
        if (lastOpenedLevel > 1) {
          setMenuOpen(openItems[openItems.length - 1], false)
        }
        event.preventDefault()
        event.stopPropagation()
      }
    })
  }

  function setMenuOpen(item: Element, isOpen: boolean) {
    const menuLevel = parseInt(item.getAttribute('data-menu-level')!)
    const link = item.querySelector('.dropdown--link')!
    const submenu = item.querySelector<HTMLElement>('.dropdown--menu')!
    const submenuOpen = submenu && submenu.matches('.-open')
    const actuallyToggling = isOpen === !submenuOpen
    if (actuallyToggling) {
      // Only reveal when *opening* — revealing on close as well scrolls the
      // page while a tap on a leaf link is still in flight (its own mousedown
      // handler closes the parent submenu first, see below), which can shift
      // the link out from under the pointer before the click fires.
      if (isOpen && isMobileInteraction()) {
        up.reveal(element, { top: true })
      }
      if (submenu) {
        submenu.style.setProperty('--parent-height', item.parentElement!.offsetHeight + 'px')
      }
    }

    link.classList.toggle('-opened', isOpen)
    if (submenu) {
      submenu.classList.toggle('-open', isOpen)
      if (isOpen && isMobileInteraction()) {
        const openingMenu = link.closest('.dropdown--menu')!
        submenu.style.minHeight = `${openingMenu.clientHeight}px`
      }
    }

    if (isOpen) {
      mobileHeaderBack.classList.toggle('-visible', true)
    } else if (openItems.length <= 1) {
      mobileHeaderBack.classList.toggle('-visible', false)
    }

    if (isOpen) {
      if (!openItems.includes(item)) {
        openItems.push(item)
        lastOpenedLevel = menuLevel + 1
        setHeaderText(link.textContent)
      }
    } else {
      if (openItems.includes(item)) {
        const index = openItems.indexOf(item)
        openItems.splice(index, 1)
        lastOpenedLevel = menuLevel
        restoreHeaderText()
      }
    }

    if (submenu) {
      requestAnimationFrame(() => {
        const scrollContainer = scrollContainers.get(submenu)
        if (scrollContainer) {
          updateScrollContainerMaxHeight(submenu, scrollContainer)
          updateScrollIndicators(scrollContainer)
        }
      })
    }
  }

  function closeAllOpenItems() {
    openItems.forEach((lastOpenedItem) => {
      setMenuOpen(lastOpenedItem, false)
    })
    lastOpenedLevel = 1
  }

  function setHeaderText(text: string) {
    const currentText = mobileHeaderText.textContent
    mobileHeaderTexts.push(currentText)
    mobileHeaderText.textContent = text
  }

  function restoreHeaderText() {
    mobileHeaderText.textContent = mobileHeaderTexts.pop() ?? ''
  }

  function isDesktopInteraction() {
    return !isMobileInteraction()
  }

  function isMobileInteraction() {
    return !(window.getComputedStyle(mobileIndicator).display === 'none')
  }

  function isNextLevel(level: string | null) {
    return level && parseInt(level) === lastOpenedLevel
  }

  function onBodyClick() {
    closeAllOpenItems()
  }

  document.addEventListener('click', onBodyClick)

  return () => {
    document.removeEventListener('click', onBodyClick)
    window.removeEventListener('resize', updateAllScrollContainers)
    containerObserver.disconnect()
  }
})
